<template>
  <div>
    <donut-page-title :title="`Automatic ${pointsNounCap}`">
      <div class="subtitle t-lg dark-gray m-tx4">
        <span>{{ subtitleCopy }}</span>
      </div>
    </donut-page-title>
    <donut-tabs :tabs="tabs" :initial-tab="initialTab" class="m-tx6" />
    <section v-if="isIntrosTab" class="m-tx6">
      <h2 class="t t-lg">What are recognition points?</h2>
      <p class="t m-tx1">
        Employees can earn recognition points by completing Donut Intros,
        celebrating milestones such as work anniversaries or birthdays, and
        receiving {{ $i18n.t('shoutouts.shoutout.other') }} from colleagues.
        Recognition points can be redeemed by customized rewards or gift cards.
        Customize rewards in the
        <a class="link c-p" href="/rewards/settings">reward store</a>.
      </p>
    </section>
    <template v-if="isIntrosTab">
      <div class="separator m-vx4" />
      <section>
        <h2 class="t t-lg">Donut Intros</h2>
        <p class="t m-tx1">
          Reward with recognition points for completing a Donut Intro call.
          These points can be redeemed if the reward store is enabled.
        </p>
      </section>
      <div class="f-ac f-gap-96 m-tx4">
        <div class="f-v flexed">
          <div class="f f-h f-ac f-sb w-100p">
            <div class="m-rx2 flexed">
              <div class="t">Enable Intros points in your workspace.</div>
            </div>
            <donut-input-toggle
              :model-value="pointsConfig[fieldName]"
              update-action="pointsConfig/updatePointsConfig"
              :update-args="{ key: fieldName }"
              size="small"
              :load-on-toggle="true"
              :paywall="paywall"
            />
          </div>
          <div :class="`f f-h f-ac f-sb w-100p m-tx3 ${!pointsConfig[fieldName] ? 'pe-n' : ''}`">
            <div class="m-rx2 flexed">
              <div class="t">Enable Intros points by default in new Intros channels.</div>
            </div>
            <donut-input-toggle
              v-if="pointsConfig[fieldName]"
              :model-value="pointsConfig[newChannelsFieldName]"
              update-action="pointsConfig/updatePointsConfig"
              :update-args="{ key: newChannelsFieldName }"
              size="small"
              :load-on-toggle="true"
              :paywall="newChannelsPaywall"
            />
            <donut-input-toggle v-else :model-value="false" size="small" :is-disabled="true" />
          </div>
        </div>
        <div class="flexed">
          <div class="t m-bx1">Intros message preview</div>
          <donut-slack-preview
            :hide-name="true"
            :message="pointsPreviewMessage"
          />
        </div>
      </div>
    </template>
    <div v-else class="f-ac f-gap-24 m-tx6">
      <div class="f-v flexed">
        <div class="f f-h f-ac f-sb w-100p">
          <div class="m-rx2 flexed">
            <div class="t">Enable giving {{ pointsNoun }} for {{ action }}.</div>
          </div>
          <donut-input-toggle
            :model-value="pointsConfig[fieldName]"
            update-action="pointsConfig/updatePointsConfig"
            :update-args="{ key: fieldName }"
            size="small"
            :load-on-toggle="true"
            :paywall="paywall"
          />
        </div>
      </div>
      <points-slack-previews-points-for-badge-received-dm v-if="props.initialTabValue === 'badges'" class="flexed" />
      <points-slack-previews-points-for-intro-received-dm v-else :points="1" class="flexed" />
    </div>
    <div v-emoji="exchangeRateText" class="m-tx2 t gray" />
  </div>
</template>

<script setup>
  import { computed, inject } from 'vue';
  import { useStore } from 'vuex';
  import { capitalize } from 'lodash';

  const $i18n = inject('$i18n');
  const { state } = useStore();

  const props = defineProps({
    initialTabValue: { type: String, default: 'intros' },
  });
  const pointsNoun = $i18n.t('shoutouts.point', { count: 2 });
  const pointsNounCap = capitalize(pointsNoun);
  const pointsConfig = computed(() => state.pointsConfig.config);
  const dollarsPerPoint = computed(() => state.pointsConfig.dollarsPerPoint);
  const fieldName = props.initialTabValue === 'badges' ? 'award_points_for_badges' : 'award_points_for_intros';
  const newChannelsFieldName = 'points_default_on_for_new_intros_channels';
  const isIntrosTab = computed(() => props.initialTabValue === 'intros');

  // Tabs
  const tabs = [
    { title: `Intros ${pointsNoun}`, value: 'intros', link: '/points/automatic' },
    { title: `Badge ${pointsNoun}`, value: 'badges', link: '/points/badges' },
  ];
  const initialTab = computed(() => tabs.find(tab => tab.value === props.initialTabValue));
  const subtitleCopy = computed(() => {
    if (isIntrosTab.value) {
      return `Employees can accumulate recognition ${pointsNoun} by participating in activities that boost company culture and achieving key milestones.`;
    }
    return `Employees can accumulate recognition ${pointsNoun} by participating in activities that boost company culture and achieving key milestones. Recognition ${pointsNoun} can be redeemed for customized rewards or gift cards. Customize rewards in the ${$i18n.t('shoutouts.rewards_store')}.`;
  });

  const paywall = computed(() => ({
      data: pointsConfig.value.upgrade_data,
      feature: fieldName,
      featureTitleText: `disable ${initialTab.value.title}`,
      source: `automatic-points-${props.initialTabValue}-main-toggle`,
      programKey: 'shoutouts',
    }));
  const newChannelsPaywall = computed(() => ({
      data: pointsConfig.value.upgrade_data,
      feature: newChannelsFieldName,
      featureTitleText: 'disable Intros points by default in new Intros channels',
      source: 'automatic-points-intros-new-channels-toggle',
      programKey: 'shoutouts',
    }));

  // Copy
  const action = props.initialTabValue === 'badges' ? 'earning Donut badges' : 'completing a Donut Intro';
  const pointsPreviewMessage = computed(() => {
    let baseMessage = `Here is 1 :${pointsConfig.value.emoji}: as a token of appreciation for attending your Donut Intro and connecting with your team!`;
    if (pointsConfig.value.active_rewards_program) {
      baseMessage += ` :${pointsConfig.value.emoji}:'s can be redeemed in the ${$i18n.t('shoutouts.rewards_store').toLowerCase()}.`;
    }
    return baseMessage;
  });
  const exchangeRateText = `Exchange rate 1 ${pointsConfig.value.points_name} = ${Math.round(dollarsPerPoint.value * 100)}¢ `
    + `:${pointsConfig.value.emoji}: applies if monetary rewards are set up.`;
</script>
